import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminAxios } from "../../axiosInstance";
import { notify } from "../../components/toastify";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BarChartIcon from "@mui/icons-material/BarChart";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import loader from "../../assets/loader.webp";

// "info" is the fallback for any type value we don't explicitly recognize.
const INSIGHT_ICONS = {
  danger: <ReportProblemIcon className="text-red-500 text-xl" />,
  warning: <WarningAmberIcon className="text-orange-500 text-xl" />,
  success: <CheckCircleIcon className="text-green-500 text-xl" />,
  info: <InfoIcon className="text-blue-500 text-xl" />,
  forecast: <BarChartIcon className="text-blue-500 text-xl" />,
  trend: <TrendingUpIcon className="text-green-500 text-xl" />,
};

const fetchAiInsights = async () => {
  const response = await adminAxios.get("ai-suggestions");
  return response?.data?.data || response?.data;
};

const generateFreshInsights = async () => {
  const response = await adminAxios.get("prompt");
  return response?.data?.data || [];
};

const formatTime = (timeInMs) => {
  const totalSeconds = Math.ceil(timeInMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pluralize = (value, unit) =>
    `${value} ${unit}${value === 1 ? "" : "s"}`;

  if (days > 0) return pluralize(days, "day");
  if (hours > 0) return pluralize(hours, "hour");
  if (minutes > 0) return pluralize(minutes, "minute");
  return pluralize(seconds, "second");
};

function AIinsight() {
  const queryClient = useQueryClient();
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [cooldownUntil]);

  const isCoolingDown = now < cooldownUntil;
  const cooldownSecondsLeft = Math.ceil((cooldownUntil - now) / 1000);
  const timeToWaitforNewAiInsights = 1 * 60 * 60 * 1000;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["ai-insights"],
    queryFn: fetchAiInsights,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const refreshMutation = useMutation({
    mutationFn: generateFreshInsights,
    onSuccess: (freshInsights) => {
      notify("Insights refreshed", "success");
      queryClient.setQueryData(["ai-insights"], { ai_insights: freshInsights });
      setCooldownUntil(Date.now() + timeToWaitforNewAiInsights);
      setTimeout(() => {
        notify(
          `Please wait ${formatTime(timeToWaitforNewAiInsights)} before refreshing again`,
          "info",
        );
      }, 2000);
    },
    onError: (err) => {
      const status = err?.response?.status;
      const message =
        status === 429
          ? "You're refreshing too often — try again in a minute."
          : err?.response?.data?.message ||
            "Couldn't refresh insights right now";
      notify(message, "error");
    },
  });

  const insights = Array.isArray(data?.ai_insights) ? data.ai_insights : [];

  return (
    <div className="bg-white p-2 rounded-lg w-full">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-gray-800">AI Insights</h3>
        <button
          onClick={() => {
            notify("Fetching new AI insights...", "info");
            refreshMutation.mutate();
          }}
          disabled={refreshMutation.isPending || isCoolingDown}
          className="flex items-center justify-center p-1.5 rounded-full hover:bg-gray-100 disabled:opacity-50 transition disabled:cursor-not-allowed"
          aria-label="Get fresh AI insights"
          title={
            refreshMutation.isPending
              ? "Getting Insights"
              : isCoolingDown
                ? `Wait ${formatTime(cooldownSecondsLeft)} before refreshing again`
                : "Get fresh insights"
          }
        >
          <AutoAwesomeIcon
            className={`text-yellow-500 ${refreshMutation.isPending ? "animate-pulse" : ""}`}
            sx={{ fontSize: "22px" }}
          />
        </button>
      </div>

      {isLoading && (
        <div className="flex justify-center py-6">
          <img src={loader} alt="loader" className="w-8" />
        </div>
      )}

      {!isLoading && isError && (
        <p className="text-sm text-gray-500 py-3">
          {error?.response?.data?.message ||
            "Couldn't load AI insights right now."}
        </p>
      )}

      {!isLoading && !isError && insights.length === 0 && (
        <p className="text-sm text-gray-500 py-3">No insights available yet.</p>
      )}

      {!isLoading && !isError && insights.length > 0 && (
        <div className="space-y-4">
          {insights.map((insight, index) => (
            <div
              key={insight.title ? `${insight.title}-${index}` : index}
              className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 shadow-md transition-colors"
            >
              <div className="flex-shrink-0">
                {INSIGHT_ICONS[insight.type] || INSIGHT_ICONS.info}
              </div>
              <div>
                {insight.title && (
                  <p className="text-sm font-semibold text-gray-800">
                    {insight.title}
                  </p>
                )}
                <p className="text-sm text-gray-700 font-medium">
                  {insight.detail}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AIinsight;
